import type { MemberRosterState } from "@/core/activity-points/types"
import type {
  GuildRankTitle,
  GuildSpecialTitle,
} from "@/core/guild-members/titles"
import type { GuildMemberDiscordPresence } from "@/core/guild-war/types"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"

export type ActivityPointsRow = {
  memberId: string
  displayName: string
  joinedAt: string | null
  discordPresence: GuildMemberDiscordPresence
  rankTitle: GuildRankTitle | null
  specialTitles: GuildSpecialTitle[]
  points: number | null
}
export type ActivityPointsMonth = {
  year: number
  month: number
  rows: ActivityPointsRow[]
}
export type SaveActivityPointsMonthInput = {
  year: number
  month: number
  entries: Array<{ memberId: string; points: number }>
}

const readError = async (response: Response, fallback: string) => {
  try {
    const payload = (await response.json()) as {
      error?: { code?: unknown; message?: unknown }
    }
    if (
      payload.error?.code === "INVALID_ACTIVITY_POINTS" &&
      typeof payload.error.message === "string"
    )
      return new Error(payload.error.message)
  } catch {}
  return new Error(fallback)
}

export const activityPointsApi = {
  async getMonth({
    year,
    month,
    state,
  }: {
    year: number
    month: number
    state: MemberRosterState
  }): Promise<ActivityPointsMonth> {
    const query = new URLSearchParams({
      year: String(year),
      month: String(month),
      state,
    })
    const response = await fetch(`/api/admin/activity-points?${query}`, {
      method: "GET",
      credentials: "include",
    })
    if (!response.ok)
      throw await readError(response, "Failed to load activity points")
    return response.json()
  },
  async saveMonth(
    input: SaveActivityPointsMonthInput,
  ): Promise<ActivityPointsMonth> {
    const csrfToken = getCsrfTokenFromDocumentCookie()
    if (!csrfToken) throw new Error("Missing CSRF token")
    const response = await fetch("/api/admin/activity-points", {
      method: "PUT",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify(input),
    })
    if (!response.ok)
      throw await readError(response, "Failed to save activity points")
    return response.json()
  },
}
